'use client';

import { Card } from '@app/app/_components/Card/Card';
import { CheckboxInput } from '@app/app/_components/Input/CheckboxInput';
import { ChoiceButtonInput } from '@app/app/_components/Input/ChoiceButtonInput';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';
import { MasterCardLogo } from '@app/app/_icons/MasterCardLogo';
import { MoneyIcon } from '@app/app/_icons/MoneyIcon';
import { PaymentMethodType } from '@app/domain/model/PaymentMethodType';
import { PaymentType } from '@app/domain/model/PaymentType';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchasePersonalInsuranceResult } from '@app/service/eshop/personal-accidents/actions/purchasePersonalAccidentsInsurance';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';

interface Props {
    formState: FormState<PurchasePersonalInsuranceResult>;
    onChange?: (paymentMethodType: Nullable<string>) => void;
}

export function PaymentMethodTypeStep({ formState, onChange }: Props) {
    const t = useTranslations();
    const parameters = useAppParameters();
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<Nullable<string>>(null);

    const choiceOptions = [
        {
            value: PaymentMethodType.Banks,
            label: (
                <div className="flex items-center gap-4">
                    <MoneyIcon className="w-10 text-primary-500" />
                    <div className="text-lg font-medium text-neutral-900">{t('eshop.general.paymentBanks')}</div>
                </div>
            ),
        },
        {
            value: PaymentMethodType.Card,
            label: (
                <div className="flex items-center gap-4">
                    <MasterCardLogo className="w-10" />
                    <div className="text-lg font-medium text-neutral-900">
                        {t('eshop.general.paymentVisaMastercard')}
                    </div>
                </div>
            ),
        },
    ];

    const handleChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            setSelectedPaymentMethod(value);
            onChange?.(value);
        },
        [onChange],
    );

    useEffect(() => {
        if (formState.data?.formData.paymentType === PaymentType.Subscription) {
            setSelectedPaymentMethod(PaymentMethodType.Card);
            return onChange?.(PaymentMethodType.Card);
        }

        setSelectedPaymentMethod(null);
        onChange?.(null);
    }, [formState.data?.formData.paymentType, onChange]);

    const getPaymentDescriptionKey = useCallback(() => {
        const paymentType = formState.data?.formData.paymentType;

        if (paymentType === PaymentType.Subscription) {
            return 'eshop.personalAccidents.paymentDescriptionSubscription';
        }

        return 'eshop.personalAccidents.paymentDescriptionOnePayment';
    }, [formState.data?.formData.paymentType]);

    const paymentDescriptionKey = getPaymentDescriptionKey();

    return (
        <div className="flex flex-col gap-10">
            {formState.data?.formData.paymentType === PaymentType.Subscription ? (
                <>
                    <input name="paymentMethodType" type="hidden" value="card" />
                    <Card className="gap-6 p-6">
                        <div className="flex items-center gap-4">
                            <MasterCardLogo className="w-10" />
                            <div className="text-lg font-medium text-neutral-900">
                                {t('eshop.general.paymentVisaMastercard')}
                            </div>
                        </div>
                        <p className="text-neutral-600">{t('eshop.general.paymentCardHint')}</p>
                    </Card>
                </>
            ) : (
                <ChoiceButtonInput
                    name="paymentMethodType"
                    inputContainerClassName="self-center"
                    onChange={handleChange}
                    options={choiceOptions}
                />
            )}
            {selectedPaymentMethod === PaymentMethodType.Card && (
                <div className="flex flex-col gap-6">
                    {paymentDescriptionKey && (
                        <Paragraph>
                            {t.rich(paymentDescriptionKey, {
                                p: chunks => <Paragraph className="mb-2 last:mb-0">{chunks}</Paragraph>,
                            })}
                        </Paragraph>
                    )}

                    <CheckboxInput
                        name="acceptCardAndPaymentProviderRules"
                        label={t.rich('eshop.general.agreeToCardAndPaymentProviderRules', {
                            link: chunks => (
                                <Link href={parameters.eshopInfoUrls.blueEmiLink} className="underline" target="_blank">
                                    {chunks}
                                </Link>
                            ),
                        })}
                        error={formState.violations?.acceptCardAndPaymentProviderRules}
                    />
                </div>
            )}
        </div>
    );
}
